import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFetch } from "../hooks/useData";
import { GlassCard, Loading, SectionTitle, Badge, EmptyState, fmtNum } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import client, { formatError } from "../lib/api";
import { Building2, Users, Database, Activity, Plus, LogIn, Ban, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLANS = ["Starter", "Professional", "Enterprise"];
const REGIONS = ["US", "EU", "India", "UAE"];
const INDUSTRIES = ["Consulting", "Healthcare", "Retail", "Banking", "Manufacturing", "Technology", "Logistics"];

function Inner() {
  const { selectTenant } = useAuth();
  const navigate = useNavigate();
  const { data: stats, loading, reload } = useFetch("/tenants/stats");
  const { data: tenants, reload: reloadTenants } = useFetch("/tenants");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "Technology", plan: "Professional", data_residency: "US", seats: 15, admin_name: "", admin_email: "", admin_password: "" });

  if (loading) return <Loading />;
  if (!stats) return null;

  const onboard = async () => {
    setSaving(true);
    try { await client.post("/tenants", form); toast.success(`${form.name} onboarded`); setOpen(false); await reload(); await reloadTenants(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (t) => { try { await client.patch(`/tenants/${t.id}`, { status: t.status === "active" ? "suspended" : "active" }); await reloadTenants(); await reload(); } catch { toast.error("Failed"); } };
  const enter = (id) => { selectTenant(id); toast.success("Entered tenant workspace"); navigate("/app"); };

  const STATS = [
    { icon: Building2, label: "Tenants", value: stats.total_tenants, accent: "pm-gradient" },
    { icon: Users, label: "Total users", value: stats.total_users, accent: "bg-gradient-to-br from-indigo-500 to-blue-500" },
    { icon: Database, label: "Datasets", value: stats.total_datasets, accent: "bg-gradient-to-br from-emerald-500 to-teal-500" },
    { icon: Activity, label: "Events processed", value: fmtNum(stats.total_events), accent: "bg-gradient-to-br from-amber-500 to-orange-500" },
  ];

  return (
    <div className="space-y-6 pm-fade-up">
      <SectionTitle eyebrow="Platform Control" title="Super Admin Console" sub="Manage tenants across the ProClytic platform"
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="onboard-tenant-btn" className="rounded-xl pm-gradient text-white border-0 h-11"><Plus className="w-4 h-4 mr-2" />Onboard tenant</Button></DialogTrigger>
            <DialogContent className="glass-strong max-w-lg">
              <DialogHeader><DialogTitle>Onboard a new tenant</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="col-span-2"><Label>Organisation name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div><Label>Industry</Label><Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="tenant-industry"><SelectValue /></SelectTrigger><SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Plan</Label><Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="tenant-plan"><SelectValue /></SelectTrigger><SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Data residency</Label><Select value={form.data_residency} onValueChange={(v) => setForm({ ...form, data_residency: v })}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="tenant-region"><SelectValue /></SelectTrigger><SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Seats</Label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: parseInt(e.target.value) || 1 })} data-testid="tenant-seats" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div className="col-span-2 border-t border-white/50 pt-2 mt-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Tenant administrator</div>
                <div><Label>Admin name</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="admin-name" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div><Label>Admin email</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="admin-email" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div className="col-span-2"><Label>Admin password</Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="admin-password" className="mt-1 bg-white/70 rounded-xl" /></div>
                <Button onClick={onboard} disabled={saving} data-testid="save-tenant" className="col-span-2 rounded-xl pm-gradient text-white border-0 mt-1 h-11">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Onboard tenant"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {STATS.map((s) => (
          <GlassCard key={s.label} className="p-5">
            <div className={`w-10 h-10 rounded-xl ${s.accent} flex items-center justify-center mb-3`}><s.icon className="w-5 h-5 text-white" /></div>
            <div className="text-3xl font-bold text-slate-900 font-display">{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">
            <th className="p-3">Tenant</th><th className="p-3">Plan</th><th className="p-3">Region</th><th className="p-3">Users</th><th className="p-3">Datasets</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {(tenants || []).map((t) => (
              <tr key={t.id} className="border-t border-white/50" data-testid={`tenant-row-${t.id}`}>
                <td className="p-3"><div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg pm-gradient-dark flex items-center justify-center text-white text-xs font-bold">{t.name.slice(0, 2).toUpperCase()}</div><div><div className="font-semibold text-slate-800">{t.name}</div><div className="text-xs text-slate-400">{t.industry}</div></div></div></td>
                <td className="p-3"><Badge value={t.plan} /></td>
                <td className="p-3 text-slate-600">{t.data_residency}</td>
                <td className="p-3 text-slate-700 font-medium">{t.user_count}</td>
                <td className="p-3 text-slate-700 font-medium">{t.dataset_count}</td>
                <td className="p-3"><Badge value={t.status} /></td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button onClick={() => enter(t.id)} data-testid={`enter-tenant-${t.id}`} size="sm" className="rounded-lg pm-gradient text-white border-0"><LogIn className="w-3.5 h-3.5 mr-1" />Enter</Button>
                    <Button onClick={() => toggle(t)} data-testid={`toggle-tenant-${t.id}`} variant="outline" size="sm" className="rounded-lg">{t.status === "active" ? <Ban className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>
    </div>
  );
}

export default function SuperAdmin() {
  const { isSuper } = useAuth();
  if (!isSuper) return <EmptyState icon={Building2} title="Restricted" sub="Super admin access only." />;
  return <Inner />;
}
